import { useLocation } from '@reach/router'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'gatsby'
import { useCartFeedback } from 'src/components/contexts/CartFeedbackContext'
import { useModalSheet } from 'src/components/contexts/ModalSheetContext'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { useMobile } from 'src/hooks/useMobile'
import { getDeviceInfo } from 'src/utils/dynamicYield/getUserDeviceObject'
import { ensureDYCookies } from 'src/utils/dynamicYield/dynamicYieldCookies'
import type { Recommendations } from 'src/components/restructure/product/types/linx-recommendations'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { InView } from 'react-intersection-observer'
import { INSURANCE_SELLER } from 'src/constants'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { Statsig } from 'src/sdk/statsig'
import {
  CASHBACK_FEEDBACK_EXPERIMENT_ID,
  PARCELAMENTO_FEEDBACK_MINICART_EXPERIMENT_ID,
} from 'src/components/common/StatsigExposure/StatsigExposure'

import { clientStore } from '../../../services/axiosInstance'
import CartFeedbackProductCardSkeleton from './CartFeedbackProductCardSkeleton'
import SimpleProductCard from './SimpleProductCard/SimpleProductCard'
import CartFeedbackInsuranceCard from './CartFeedbackInsuranceCard'
import CartFeedbackCashback from './CartFeedbackCashback'
import CartFeedbackInstallmentNudge from './InstallmentNudge/CartFeedbackInstallmentNudge'
import { MINICART_CROSS_SELL_PRODUCT_LIST_NAME } from './constants'
import { useCartFeedbackCashback } from './useCartFeedbackCashback'

const CartFeedbackContent = () => {
  const { href, pathname } = useLocation()
  const { isMobile } = useMobile()
  const { orderForm } = useCheckout()
  const { closeModal } = useModalSheet()
  const { closeCartFeedback, idsToShow } = useCartFeedback()
  const { pageData } = useGlobalContext()
  const isInstallmentNudgeEnabled = Statsig.useStatsigFeatureGateValue(
    PARCELAMENTO_FEEDBACK_MINICART_EXPERIMENT_ID
  )

  const isCashbackFeedbackEnabled = Statsig.useStatsigFeatureGateValue(
    CASHBACK_FEEDBACK_EXPERIMENT_ID
  )

  const standardObj = getStandardObject(pageData)
  const { cashbackValue, hasCashback } = useCartFeedbackCashback({
    orderFormId: orderForm?.orderFormId,
    cartItems: orderForm?.items ?? [],
  })

  const [crossSellItems, setCrossSellItems] = useState<Recommendations | null>(
    null
  )

  const [isLoading, setIsLoading] = useState(true)

  const lastItemAdded = useMemo(() => {
    return idsToShow?.map((id) =>
      orderForm?.items?.find((item) => item.id === id)
    )
  }, [idsToShow, orderForm?.items])

  useEffect(() => {
    if (isMobile === undefined) {
      return
    }

    async function fetchRecommendations() {
      try {
        const productIds = lastItemAdded?.map((item) => ({
          productId: item?.productId,
          skuId: item?.id,
        }))

        if (!productIds?.length) {
          return
        }

        const device = await getDeviceInfo()
        const { dyid, sessionId } = await ensureDYCookies()
        const params = {
          dyuser: dyid,
          dysession: sessionId,
          name: 'minicart',
          location: href,
          source: isMobile ? 'mobile' : 'desktop',
          engine: 'dy',
          device,
          productId: productIds?.at(-1)?.productId,
          skuId: productIds?.at(-1)?.skuId,
        }

        const { data } = await clientStore.get(
          '/_v3/api/decathlon-search/pages/recommendations',
          {
            params,
          }
        )

        setCrossSellItems(
          data?.[0]?.showcases?.[0]?.displays?.[0]?.recommendations
        )
      } catch (err) {
        console.error('Erro no DY:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchRecommendations()
  }, [href, isMobile, lastItemAdded, orderForm?.items])

  const sendFeedbackViewed = () => {
    dispatchAmplitudeEvent({
      eventName: 'Toast Viewed',
      eventData: { 'toast name': 'Adicionado ao carrinho!' },
      ...standardObj,
    })
  }

  const handleClose = () => {
    closeModal()
    closeCartFeedback()
  }

  return (
    <InView
      as="div"
      className="flex flex-col h-[calc(100%-80px)] justify-between px-4"
      onChange={(inView) => {
        if (inView) {
          sendFeedbackViewed()
        }
      }}
    >
      <div className="block absolute top-2 left-1/2 -translate-x-1/2 h-1 w-[101px] bg-gray rounded-md sm:hidden" />
      <div className="border-b border-gray pb-3">
        <p
          className="text-base mb-4 leading-[100%] font-medium"
          data-testid="cart-feedback-title"
        >
          Adicionado ao carrinho!
        </p>
        {lastItemAdded?.map((item) => {
          if (!item) {
            return null
          }

          const {
            productName,
            additionalInformation: { color, size },
            imageUrl,
            id,
            sellerName,
            price,
          } = item

          if (sellerName === INSURANCE_SELLER) {
            return (
              <CartFeedbackInsuranceCard
                name={productName}
                price={price}
                onClick={handleClose}
              />
            )
          }

          return (
            <div
              key={`cart-feedback-item-${id}`}
              className="flex gap-3 font-inter text-restructure-primary font-medium mb-3"
            >
              <div className="max-w-[64px] max-h-[64px] bg-neutral02">
                <img
                  className="mix-blend-multiply"
                  src={imageUrl}
                  alt={productName}
                />
              </div>
              <div className="flex flex-col justify-center">
                <p className="text-xs max-w-[222px] mb-2 overflow-hidden text-ellipsis whitespace-nowrap">
                  {productName}
                </p>
                <span className="text-xs">
                  {color && color !== 'N/A' && `Cor: ${color} | `}
                  {''}
                  {size && size !== 'N/A' && `Tamanho: ${size}`}
                </span>
              </div>
            </div>
          )
        })}
        {isCashbackFeedbackEnabled && hasCashback && (
          <CartFeedbackCashback cashbackValue={cashbackValue} />
        )}
      </div>

      <div className="flex-1 min-h-44 mt-9 sm:mt-10 w-full h-full">
        {!isLoading && !crossSellItems?.length && isInstallmentNudgeEnabled && (
          <div className="mt-1 mb-4 md:mb-12 hidden md:block">
            <CartFeedbackInstallmentNudge
              totalCartLabel={orderForm?.totalizers?.total}
            />
          </div>
        )}
        {isLoading ? (
          <CartFeedbackProductCardSkeleton />
        ) : (
          !!crossSellItems?.length && (
            <div className="pb-3 flex flex-col h-full">
              <p className="text-base font-medium text-restructure-primary">
                Complete sua compra
              </p>

              <div className="cross-selling-container max-h-[calc(100vh-380px)] mt-2 overflow-scroll sm:border-b sm:border-b-gray -mr-4 sm:mr-0">
                <ul className="flex sm:flex-col gap-2 pr-[10px]">
                  {crossSellItems?.map((recommendation) => (
                    <li key={recommendation.productId}>
                      <SimpleProductCard
                        product={recommendation}
                        shelfRef={MINICART_CROSS_SELL_PRODUCT_LIST_NAME}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )
        )}
        {!isLoading && isInstallmentNudgeEnabled && (
          <div className="mt-1 mb-4 block md:hidden">
            <CartFeedbackInstallmentNudge
              totalCartLabel={orderForm?.totalizers?.total}
            />
          </div>
        )}
      </div>

      <div
        className={`flex items-end justify-end flex-col gap-3 text-base font-semibold pb-8 `}
      >
        {!isLoading && !!crossSellItems?.length && isInstallmentNudgeEnabled && (
          <div className="mt-1 mb-4 md:mb-12 hidden md:block">
            <CartFeedbackInstallmentNudge
              totalCartLabel={orderForm?.totalizers?.total}
            />
          </div>
        )}
        {pathname !== '/checkout/cart' && (
          <Link
            onClick={() => {
              closeModal()
              closeCartFeedback()
            }}
            to="/checkout/cart"
            className="w-full h-12 flex items-center justify-center rounded-round text-white bg-restructure-background-action-1"
            data-testid="go-to-cart-button"
          >
            Ir para o carrinho
          </Link>
        )}
        <button
          onClick={handleClose}
          className="w-full h-12 flex items-center justify-center rounded-round text-primaryBlue bg-restructure-background-neutral-3"
        >
          Continuar comprando
        </button>
      </div>
    </InView>
  )
}

export default CartFeedbackContent
